import { type RefObject, useRef, useState } from 'react';
import type { SheetBehaviourProps, SheetEngineProps } from '../sheetTypes';
import useSheetHandlers, { type SheetHandlers } from './useSheetHandlers';
import useSheetInitialisation from './useSheetInitialisation';
import useSheetSnapPointState from './useSheetSnapPointState';

export interface SheetContextState extends SheetHandlers {
    sheetContentRef: RefObject<HTMLDivElement>;
    sheetContainerRef: RefObject<HTMLDivElement>;
    sheetRef: RefObject<HTMLDivElement>;
    snapPointsState: number[];
    isSheetMounted: boolean;
    setIsSheetMounted: (isMounted: boolean) => void;
    requestClose: () => void;
}

const useSheetContextState = (props: SheetBehaviourProps): SheetContextState => {
    const sheetContainerRef = useRef<HTMLDivElement>(null);
    const sheetRef = useRef<HTMLDivElement>(null);
    const internalContentRef = useRef<HTMLDivElement>(null);

    // Consumers can supply their own content ref when they need to read or focus the scroll container
    const sheetContentRef = props.contentRef ?? internalContentRef;

    const sheetProps: SheetEngineProps = { ...props, sheetContainerRef, sheetRef, sheetContentRef };

    const [isSheetMounted, setIsSheetMounted] = useState(false);

    const { snapPointsState, addSnapPoint } = useSheetSnapPointState(sheetProps);

    // Managed by useSheetHandlers
    const cancelInProgressDragAnimationRef = useRef<(() => void) | null>(null);

    // Managed by useSheetInitialisation
    const cancelInProgressCloseTransitionRef = useRef<(() => void) | null>(null);

    // Managed by useSheetInitialisation > setInitialSheetHeight
    const cancelInitialHeightAnimationFrameRef = useRef<(() => void) | null>(null);

    const cancelInProgressAnimations = () => {
        cancelInProgressCloseTransitionRef.current?.();

        // Cancel any in-progress drag animations that run from when sheet drag ends until it reaches the snap point
        cancelInProgressDragAnimationRef.current?.();

        cancelInitialHeightAnimationFrameRef.current?.();
    };

    const sheetInitialised = useSheetInitialisation(
        sheetProps,
        addSnapPoint,
        isSheetMounted,
        setIsSheetMounted,
        cancelInProgressCloseTransitionRef,
        cancelInitialHeightAnimationFrameRef,
        cancelInProgressAnimations,
    );

    const handlers = useSheetHandlers(
        sheetProps,
        sheetInitialised,
        snapPointsState,
        cancelInProgressDragAnimationRef,
        setIsSheetMounted,
    );

    return {
        ...handlers,
        sheetContainerRef,
        sheetContentRef,
        sheetRef,
        snapPointsState,
        requestClose: () => props.onOpenChange(false),
        isSheetMounted,
        setIsSheetMounted,
    };
};

export default useSheetContextState;
